import { Injectable } from '@angular/core';
import { Actions, createEffect, Effect, ofType } from '@ngrx/effects';
import * as homeActions from '../actions/post.action';
import {
  CreateReplyCommentError,
  CreateReplyCommentSuccess,
  DeleteReplyComment,
  HomeActionTypes,
  LoadMoreReplyComment,
  UpVoteReplyComment
} from '../actions/post.action';
import {
  catchError,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { of } from 'rxjs';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import * as fromHome from '../index';
import { getPostsState } from '../index';
import { PostState } from '../states/post.state';
import { NotificationService } from '../../../core/notifications/notification.service';
import { PostService } from '../../shared-home/services/post.service';
import { NewPostMutationInput } from '../../../shared/data/inputs/new-post-mutation.input';
import { PostQueryInput } from '../../../shared/data/inputs/post-query.input';
import { UserPostService } from '../../shared-home/services/user-post.service';
import { UserPostInput } from '../../../shared/data/inputs/user-post.input';
import { FavoritePostInput } from '../../../shared/data/inputs/favorite-post.input';
import { PostCommentReplyInput } from '../inputs/post-comment-reply.input';
import { GetCommentsRepliesInput } from '../../../shared/data/inputs/get-comments-replies.input';
import { VoteCommentInput } from '../../../shared/data/inputs/vote-comment.input';
import { CommentInput } from '../../../shared/data/inputs/comment.input';

@Injectable()
export class HomeEffects {
  constructor(
    private actions$: Actions,
    private postService: PostService,
    private router: Router,
    private store$: Store<fromHome.State>,
    private notificationService: NotificationService,
    private userPostService: UserPostService
  ) {}

  @Effect()
  loadTopFeeds$ = this.actions$.pipe(
    ofType(HomeActionTypes.LoadTopFeed),
    switchMap((action: homeActions.LoadTopFeed) =>
      this.postService.getPosts(action.payload).pipe(
        tap(r => console.error(r)),
        map(res => {
          // subscribe to timeline
          // console.error(res.data.timeline.subscriptionToken);
          // this.store$.dispatch(new homeActions.TimelineSubscribe({
          //   tknStr: res.data.timeline.subscriptionToken
          // }));
          if (res.data) {
            const timeline = res.data.timeline;
            return new homeActions.LoadPostSuccess({
              pageInfo: timeline.pageInfo,
              posts: timeline.data
            });
          }
          return new homeActions.LoadPostError(res.errors[0].message);
        }),
        catchError(err => {
          return of(new homeActions.LoadPostError(err));
        })
      )
    )
  );

  @Effect()
  createNormalPost$ = this.actions$.pipe(
    ofType<homeActions.CreateNormalPost>(HomeActionTypes.CreateNormalPost),
    withLatestFrom(this.store$.select(fromHome.getPostFormState)),
    switchMap(([action, postState]) => {
      const data: NewPostMutationInput = {
        ...action.payload,
        images: postState.uploadFiles
          .map(p => p.uploadUrl)
          .filter(f => f !== ''),
        videos: postState.uploadVideos
          .filter(v => v?.status === 200)
          .map(v => {
            const low_q = 'https://res.cloudinary.com/gscroll/video/upload/q_auto/'.concat(
              v?.data?.secure_url.substr(48)
            );
            return low_q;
          })
      };

      return this.postService.postNormalPosts(data).pipe(
        map(res => {
          if (res.errors) {
            const error = res.errors[0].message;
            this.notificationService.error(
              'Sorry, an error occurred while scribing.'
            );
            console.error(error);
            return new homeActions.CreateNormalPostError(error);
          }
          this.notificationService.success('Scribe Successful');
          return new homeActions.CreateNormalPostSuccess(res.data.createPost);
        }),
        catchError(err => {
          console.error(err);
          this.notificationService.error('Sorry, an error occurred, try again');
          return of(new homeActions.CreateNormalPostError(err));
        }),
        tap(ac => {
          if (ac instanceof homeActions.CreateNormalPostSuccess) {
            const resourceType = action?.payload?.privateResourceName;

            if (resourceType === 'GROUP') {
              this.router.navigateByUrl(
                `/group/${action?.payload?.privateResourceID}/newest`
              );
            }
            if (
              resourceType !== 'GROUP' &&
              resourceType !== 'FOLLOWERS' &&
              resourceType !== 'FRIENDS'
            ) {
              this.router.navigate(['/home']);
            }
          }
        })
      );
    })
  );

  @Effect()
  CreateEchoPost$ = this.actions$.pipe(
    ofType<homeActions.CreateEchoPost>(HomeActionTypes.CreateEchoPost),
    map(action => action.payload),
    switchMap(payload => {
      const data: NewPostMutationInput = {
        ...payload?.input,
        body: payload?.input?.body || ' '
      };

      return this.postService.postNormalPosts(data).pipe(
        map(res => {
          if (res.errors) {
            this.notificationService.error(
              'An error occurred while Rescribing.'
            );
            console.error(res.errors);
            return new homeActions.CreateEchoPostError(res.errors[0].message);
          }
          this.notificationService.success('Rescribed');
          if (payload?.showOnFeed) {
            return new homeActions.CreateEchoPostSuccess(res.data.createPost);
          }
          return new homeActions.JustEchoSuccess();
        }),
        catchError(err => {
          console.error(err);
          this.notificationService.error('An error occurred while Rescribing.');
          return of(new homeActions.CreateEchoPostError(err));
        })
      );
    })
  );

  @Effect()
  createComment$ = this.actions$.pipe(
    ofType(HomeActionTypes.CreateComment),
    switchMap((action: homeActions.CreateComment) =>
      this.postService.createComment(action.payload).pipe(
        map(res => {
          if (res.errors) {
            return new homeActions.CreateCommentError(res.errors[0].message);
          }

          console.error(`Inside effect ${action.payload.postID}`);
          return new homeActions.CreateCommentSuccess({
            postId: action.payload.postID,
            comment: res.data.createComment
          });
        }),
        catchError(err => of(new homeActions.CreateCommentError(err)))
      )
    )
  );

  @Effect()
  upVotePost$ = this.actions$.pipe(
    ofType(HomeActionTypes.UpVotePost),
    switchMap((action: homeActions.UpVotePost) =>
      this.postService.upVotePost(action.payload).pipe(
        map(res => {
          if (res.errors) {
            return new homeActions.VotePostError({
              postId: action.payload.postID,
              message: res.errors[0].message
            });
          }

          if (!res.data.upVotePost.operationResult) {
            return new homeActions.VotePostError({
              postId: action.payload.postID,
              message: 'Error upvoting'
            });
          }
          return new homeActions.VotePostSuccess(res.data.upVotePost);
        }),
        catchError(err =>
          of(
            new homeActions.VotePostError({
              message: err,
              postId: action.payload.postID
            })
          )
        )
      )
    )
  );

  @Effect()
  downVotePost$ = this.actions$.pipe(
    ofType(HomeActionTypes.DownVotePost),
    switchMap((action: homeActions.DownVotePost) =>
      this.postService.downVotePost(action.payload).pipe(
        map(res => {
          if (res.errors) {
            return new homeActions.VotePostError({
              postId: action.payload.postID,
              message: res.errors[0].message
            });
          }
          if (!res.data.downVotePost.operationResult) {
            return new homeActions.VotePostError({
              postId: action.payload.postID,
              message: 'Error downvoting'
            });
          }
          return new homeActions.VotePostSuccess(res.data.downVotePost);
        }),
        catchError(err =>
          of(
            new homeActions.VotePostError({
              message: err,
              postId: action.payload.postID
            })
          )
        )
      )
    )
  );

  @Effect()
  UnVotePost$ = this.actions$.pipe(
    ofType<homeActions.UnVotePost>(HomeActionTypes.UnVotePost),
    map(action => action.payload),
    switchMap(payload =>
      this.postService.unVotePost(payload).pipe(
        map(res => {
          if (res.errors) {
            return new homeActions.VotePostError({
              postId: payload.postID,
              message: res.errors[0].message
            });
          }
          if (!res.data.unVotePost.operationResult) {
            return new homeActions.VotePostError({
              postId: payload.postID,
              message: 'Error upvoting'
            });
          }
          return new homeActions.VotePostSuccess(res.data.unVotePost);
        }),
        catchError(err =>
          of(
            new homeActions.VotePostError({
              message: err,
              postId: payload.postID
            })
          )
        )
      )
    )
  );

  @Effect()
  upVoteComment$ = this.actions$.pipe(
    ofType(HomeActionTypes.UpVoteComment),
    switchMap((action: homeActions.UpVoteComment) => {
      return this.postService.upVoteComment(action.payload).pipe(
        map(res => {
          if (res.errors) {
            return new homeActions.VoteCommentError({
              error: res.errors[0].message,
              postID: action.payload.postID,
              commentID: action.payload.commentID
            });
          }
          return new homeActions.VoteCommentSuccess({
            payload: res.data.upVoteComment.modifiedComment,
            commentID: action.payload.commentID,
            postID: action.payload.postID
          });
        }),
        catchError(err =>
          of(
            new homeActions.VoteCommentError({
              error: err,
              commentID: action.payload.commentID,
              postID: action.payload.postID
            })
          )
        )
      );
    })
  );

  @Effect()
  downVoteComment$ = this.actions$.pipe(
    ofType(HomeActionTypes.DownVoteComment),
    switchMap((action: homeActions.DownVoteComment) => {
      return this.postService.downVoteComment(action.payload).pipe(
        map(res => {
          if (res.errors) {
            return new homeActions.VoteCommentError({
              error: res.errors[0].message,
              postID: action.payload.postID,
              commentID: action.payload.commentID
            });
          }
          if (!res.data.downVoteComment.operationResult) {
            return new homeActions.VoteCommentError({
              error: 'Operation was not successful',
              postID: action.payload.postID,
              commentID: action.payload.commentID
            });
          }

          return new homeActions.VoteCommentSuccess({
            payload: res.data.downVoteComment.modifiedComment,
            commentID: action.payload.commentID,
            postID: action.payload.postID
          });
        }),
        catchError(err =>
          of(
            new homeActions.VoteCommentError({
              error: err,
              commentID: action.payload.commentID,
              postID: action.payload.postID
            })
          )
        )
      );
    })
  );

  @Effect()
  unVoteComment$ = this.actions$.pipe(
    ofType<homeActions.UnVoteComment>(HomeActionTypes.UnVoteComment),
    switchMap(action => {
      return this.postService.unVoteComment(action.payload).pipe(
        map(res => {
          if (res.errors) {
            return new homeActions.VoteCommentError({
              error: res.errors[0].message,
              postID: action.payload.postID,
              commentID: action.payload.commentID
            });
          }
          if (!res.data.unVoteComment.operationResult) {
            return new homeActions.VoteCommentError({
              error: 'Operation was not successful',
              postID: action.payload.postID,
              commentID: action.payload.commentID
            });
          }

          return new homeActions.VoteCommentSuccess({
            payload: res.data.unVoteComment.modifiedComment,
            commentID: action.payload.commentID,
            postID: action.payload.postID
          });
        }),
        catchError(err =>
          of(
            new homeActions.VoteCommentError({
              error: err,
              commentID: action.payload.commentID,
              postID: action.payload.postID
            })
          )
        )
      );
    })
  );

  @Effect()
  showComments$ = this.actions$.pipe(
    ofType(HomeActionTypes.ShowComments),
    switchMap((action: homeActions.ShowComments) =>
      this.postService.getPostComment(action.payload).pipe(
        map(res => {
          if (res.errors) {
            return new homeActions.ShowCommentsError({
              message: res.errors[0].message,
              postId: action.payload.postID
            });
          }
          return new homeActions.ShowCommentsSuccess(res.data.post);
        }),
        catchError(err => {
          return of(
            new homeActions.ShowCommentsError({
              postId: action.payload.postID,
              message: err
            })
          );
        })
      )
    )
  );

  @Effect()
  timeSubscription$ = this.actions$.pipe(
    ofType<homeActions.TimelineSubscribe>(HomeActionTypes.TimelineSubscribe),
    map(action => action.payload),
    switchMap(payload =>
      this.postService.getTimeLineSubscription(payload).pipe(
        map(res => {
          if (res.errors) {
            return new homeActions.TimelineSubscribeError(
              res.errors[0].message
            );
          }
          console.error(res.data.timeline);
          return new homeActions.TimelineSubscribeSuccess(res.data.timeline);
        }),
        catchError(err => of(new homeActions.TimelineSubscribeError(err)))
      )
    )
  );

  @Effect()
  loadMoreComments$ = this.actions$.pipe(
    ofType<homeActions.LoadMoreComment>(HomeActionTypes.LoadMoreComment),
    map(action => action.payload),
    switchMap(payload =>
      this.postService.getPostComment(payload).pipe(
        map(res => {
          if (res.errors) {
            return new homeActions.LoadMoreCommentError({
              message: res.errors[0].message,
              postId: payload.postID
            });
          }
          return new homeActions.LoadMoreCommentSuccess(res.data.post);
        }),
        catchError(err =>
          of(
            new homeActions.LoadMoreCommentError({
              postId: payload.postID,
              message: err
            })
          )
        )
      )
    )
  );

  @Effect()
  deletePost$ = this.actions$.pipe(
    ofType<homeActions.DeletePost>(HomeActionTypes.DeletePost),
    map(action => action.payload),
    switchMap(payload =>
      this.postService.deletePost(payload).pipe(
        map(res => {
          if (res.errors) {
            return new homeActions.DeletePostError({
              postId: payload.postID,
              message: res.errors[0].message
            });
          }
          const deletedResp = res.data.deletePost;
          if (!deletedResp) {
            return new homeActions.DeletePostError({
              postId: payload.postID,
              message: 'An error occurred while deleting your scribe'
            });
          }
          this.notificationService.warn('Post deleted');
          return new homeActions.DeletePostSuccess({
            postId: payload.postID,
            operationResult: deletedResp
          });
        }),
        catchError(err =>
          of(
            new homeActions.DeletePostError({
              postId: payload.postID,
              message: err
            })
          )
        )
      )
    )
  );

  @Effect()
  deleteComment$ = this.actions$.pipe(
    ofType<homeActions.DeleteComment>(HomeActionTypes.DeleteComment),
    map(action => action.payload),
    switchMap(payload =>
      this.postService.deleteComment(payload.commentInput).pipe(
        map(res => {
          if (res.errors) {
            return new homeActions.DeleteCommentError({
              commentId: payload.commentInput.commentID,
              message: res.errors[0].message,
              postId: payload.postId
            });
          }

          if (res.data.deleteComment.operationResult) {
            return new homeActions.DeleteCommentSuccess({
              postId: payload.postId,
              commentId: payload.commentInput.commentID
            });
          } else {
            return new homeActions.DeleteCommentError({
              commentId: payload.commentInput.commentID,
              message: 'Comment was not deleted',
              postId: payload.postId
            });
          }
        }),
        catchError(err => {
          console.error(err);
          return of(
            new homeActions.DeleteCommentError({
              commentId: payload.commentInput.commentID,
              message: err,
              postId: payload.postId
            })
          );
        })
      )
    )
  );

  loadMoreTimeline$ = createEffect(() =>
    this.actions$.pipe(
      ofType<homeActions.LoadMoreTimeline>(HomeActionTypes.LoadMorePost),
      map(action => action.payload),
      withLatestFrom(this.store$.select(fromHome.getPostsState)),
      switchMap(([payload, postState]: [PostQueryInput, PostState[]]) => {
        // get the last post and use it to load more
        const data: PostQueryInput = {
          ...payload,
          olderThan: postState[postState.length - 1].post.Id
        };

        return this.postService.getPosts(data).pipe(
          map(res => {
            if (res.data) {
              const timeline = res?.data?.timeline;
              return new homeActions.LoadMorePostSuccess({
                pageInfo: timeline.pageInfo,
                posts: timeline?.data
              });
            }
            return new homeActions.LoadMorePostError(res.errors[0].message);
          }),
          catchError(err => of(new homeActions.LoadMorePostError(err)))
        );
      })
    )
  );

  // favourite post
  favoritePost$ = createEffect(() =>
    this.actions$.pipe(
      ofType<homeActions.FavoritePost>(HomeActionTypes.FavoritePost),
      map(action => action.payload),
      switchMap(payload =>
        this.postService.favoritePost(payload).pipe(
          map(res => {
            if (res.errors) {
              return new homeActions.FavoritePostError({
                postId: payload.postID,
                message: res.errors[0].message
              });
            }
            return new homeActions.FavoritePostSuccess(
              res.data.favoritePost.modifiedPost
            );
          }),
          catchError(err =>
            of(
              new homeActions.FavoritePostError({
                message: err,
                postId: payload.postID
              })
            )
          )
        )
      )
    )
  );

  unFavoritePost$ = createEffect(() =>
    this.actions$.pipe(
      ofType<homeActions.UnFavoritePost>(HomeActionTypes.UnFavoritePost),
      map(action => action.payload),
      switchMap(payload =>
        this.postService.unFavoritePost(payload).pipe(
          map(res => {
            if (res.errors) {
              return new homeActions.FavoritePostError({
                postId: payload.postID,
                message: res.errors[0].message
              });
            }
            return new homeActions.FavoritePostSuccess(
              res.data.unFavoritePost.modifiedPost
            );
          }),
          catchError(err =>
            of(
              new homeActions.FavoritePostError({
                message: err,
                postId: payload.postID
              })
            )
          )
        )
      )
    )
  );

  castPollPost$ = createEffect(() =>
    this.actions$.pipe(
      ofType<homeActions.VotePostPoll>(HomeActionTypes.VotePostPoll),
      map(action => action.payload),
      switchMap(payload =>
        this.postService.castPostPoll(payload).pipe(
          map(res => {
            if (res.errors) {
              return new homeActions.VotePostPollError({
                postId: payload.postID,
                message: res.errors[0].message
              });
            }
            return new homeActions.VotePostPollSuccess(
              res.data.castPollVote.modifiedPost
            );
          }),
          catchError(err =>
            of(
              new homeActions.VotePostPollError({
                message: err,
                postId: payload.postID
              })
            )
          )
        )
      )
    )
  );

  // user post content

  @Effect()
  loadUserPosts$ = this.actions$.pipe(
    ofType<homeActions.LoadUserPost>(HomeActionTypes.LoadUserPost),
    map(action => action.payload),
    switchMap(payload =>
      this.userPostService.getUserPosts(payload).pipe(
        map(res => {
          if (res.data) {
            return new homeActions.LoadPostSuccess({
              posts: res.data.userPosts.data,
              pageInfo: res.data.userPosts.pageInfo
            });
          }
          return new homeActions.LoadPostError(res.errors[0].message);
        }),
        catchError(err => {
          console.error(err);
          return of(new homeActions.LoadPostError(err));
        })
      )
    )
  );

  @Effect()
  loadMoreUserPostEffect$ = this.actions$.pipe(
    ofType<homeActions.LoadMoreUserPost>(HomeActionTypes.LoadMoreUserPost),
    map(action => action.payload),
    withLatestFrom(this.store$.select(getPostsState)),
    switchMap(([payload, postState]) => {
      const input: UserPostInput = {
        ...payload,
        after: postState[postState.length - 1].post.Id
      };

      return this.userPostService.getUserPosts(input).pipe(
        map(res => {
          if (res.errors) {
            return new homeActions.LoadPostError(res.errors[0].message);
          }
          return new homeActions.LoadMorePostSuccess({
            pageInfo: res?.data?.userPosts?.pageInfo || null,
            posts: res?.data?.userPosts?.data
          });
        }),
        catchError(err => {
          console.error(err);
          return of(new homeActions.LoadPostError(err.toString()));
        })
      );
    })
  );

  @Effect()
  loadUserFavorite$ = this.actions$.pipe(
    ofType<homeActions.LoadUserFavorite>(HomeActionTypes.LoadUserFavorite),
    map(action => action.payload),
    switchMap(payload =>
      this.userPostService.getUserFavoritePosts(payload).pipe(
        map(res => {
          if (res.data) {
            return new homeActions.LoadPostSuccess({
              posts: res.data.userFavorites.data,
              pageInfo: res.data.userFavorites.pageInfo
            });
          }
          return new homeActions.LoadPostError(res.errors[0].message);
        }),

        catchError(err => {
          console.error(err);
          return of(new homeActions.LoadPostError(err));
        })
      )
    )
  );

  @Effect()
  loadMoreUserFavorite$ = this.actions$.pipe(
    ofType<homeActions.LoadMoreUserFavorite>(
      HomeActionTypes.LoadMoreUserFavorite
    ),
    map(action => action.payload),
    withLatestFrom(this.store$.select(getPostsState)),
    switchMap(([payload, postState]) => {
      const input: FavoritePostInput = {
        ...payload,
        after: postState[postState.length - 1].post.Id
      };

      return this.userPostService.getUserFavoritePosts(input).pipe(
        map(res => {
          if (res.errors) {
            return new homeActions.LoadPostError(res.errors[0].message);
          }
          return new homeActions.LoadMorePostSuccess({
            pageInfo: res?.data?.userFavorites?.pageInfo || null,
            posts: res?.data?.userFavorites?.data
          });
        }),
        catchError(err => {
          console.error(err);
          return of(new homeActions.LoadPostError(err.toString()));
        })
      );
    })
  );

  @Effect()
  loadExpandedPost$ = this.actions$.pipe(
    ofType<homeActions.LoadExpandedPost>(HomeActionTypes.LoadExpandedPost),
    map(action => action.payload),
    switchMap(payload =>
      this.postService.getSinglePostWithComments(payload).pipe(
        map(res => {
          if (res.errors) {
            console.error(res.errors);
            return new homeActions.LoadPostError(res.errors[0].message);
          }
          return new homeActions.LoadExpandedPostSuccess(res.data.post);
        }),
        catchError(err => {
          console.error(err);
          return of(new homeActions.LoadPostError(err));
        })
      )
    )
  );

  @Effect()
  showReplyComment$ = this.actions$.pipe(
    ofType<homeActions.ShowReplyComment>(HomeActionTypes.ShowReplyComment),
    map(action => action.payload),
    mergeMap(payload =>
      this.postService.getCommentReply(payload?.input).pipe(
        map(res => {
          if (res.errors) {
            return new homeActions.ShowReplyCommentError({
              error: res.errors[0].message,
              commentID: payload?.commentID,
              postID: payload?.postID
            });
          }
          return new homeActions.ShowReplyCommentSuccess({
            commentID: payload?.commentID,
            postID: payload?.postID,
            output: res.data.getCommentReplies
          });
        }),
        catchError(error => {
          console.error(error);
          return of(
            new homeActions.ShowReplyCommentError({
              error: 'An error occurred please try again',
              commentID: payload?.commentID,
              postID: payload?.postID
            })
          );
        })
      )
    )
  );

  @Effect()
  createCommentReply$ = this.actions$.pipe(
    ofType<homeActions.CreateReplyComment>(HomeActionTypes.CreateReplyComment),
    map(action => action.input),
    switchMap(input =>
      this.postService.createComment(input.payload).pipe(
        map(res => {
          if (res.errors) {
            return new CreateReplyCommentError({
              postID: input.postID,
              commentID: input.commentID,
              payload: res.errors[0].message
            });
          }

          return new CreateReplyCommentSuccess({
            postID: input.postID,
            commentID: input.commentID,
            payload: res.data.createComment
          });
        }),
        catchError(error => {
          console.error(error);
          return of(
            new CreateReplyCommentError({
              postID: input.postID,
              commentID: input.commentID,
              payload: 'An error occurred while creating reply'
            })
          );
        })
      )
    )
  );

  @Effect()
  loadMoreReplyComment$ = this.actions$.pipe(
    ofType<LoadMoreReplyComment>(HomeActionTypes.LoadMoreReplyComment),
    map(action => action.payload),
    withLatestFrom(this.store$.select(getPostsState)),
    switchMap(([payload, postStates]: [PostCommentReplyInput, PostState[]]) => {
      const postState =
        postStates?.filter(p => p?.post?.Id === payload?.postID)[0] || null;

      const currentComment =
        postState?.commentsState?.filter(
          c => c?.comment?.Id === payload?.commentID
        )[0] || null;

      const afterID = currentComment?.replyPageInfo?.endCursor;

      const input: GetCommentsRepliesInput = {
        ...payload,
        parentCommentID: payload?.commentID,
        after: afterID
      };

      return this.postService.getCommentReply(input).pipe(
        map(res => {
          if (res.errors) {
            return new homeActions.ShowReplyCommentError({
              error: res?.errors[0].message,
              commentID: payload?.commentID,
              postID: payload?.postID
            });
          }
          return new homeActions.LoadMoreReplyCommentSuccess({
            commentID: payload?.commentID,
            postID: payload?.postID,
            output: res?.data?.getCommentReplies
          });
        }),
        catchError(error => {
          console.error(error);
          return of(
            new homeActions.ShowReplyCommentError({
              error: 'An error occurred please try again',
              commentID: payload?.commentID,
              postID: payload?.postID
            })
          );
        })
      );
    })
  );

  @Effect()
  upVoteReplyComment$ = this.actions$.pipe(
    ofType<UpVoteReplyComment>(HomeActionTypes.UpVoteReplyComment),
    map(action => action.payload),
    switchMap(payload => {
      const input: VoteCommentInput = {
        commentID: payload?.replyCommentID,
        postID: payload?.postID
      };

      return this.postService.upVoteComment(input).pipe(
        map(res => {
          if (res?.errors) {
            return new homeActions.VoteReplyCommentError({
              ...payload,
              payload: res?.errors[0].message
            });
          }
          return new homeActions.VoteReplyCommentSuccess({
            ...payload,
            output: res?.data?.upVoteComment?.modifiedComment
          });
        }),
        catchError(error => {
          console.error(error);
          return of(
            new homeActions.VoteReplyCommentError({
              ...payload,
              payload: error
            })
          );
        })
      );
    })
  );

  @Effect()
  downVoteReplyComment$ = this.actions$.pipe(
    ofType<UpVoteReplyComment>(HomeActionTypes.DownVoteReplyComment),
    map(action => action.payload),
    switchMap(payload => {
      const input: VoteCommentInput = {
        commentID: payload?.replyCommentID,
        postID: payload?.postID
      };

      return this.postService.downVoteComment(input).pipe(
        map(res => {
          if (res?.errors) {
            return new homeActions.VoteReplyCommentError({
              ...payload,
              payload: res?.errors[0].message
            });
          }
          return new homeActions.VoteReplyCommentSuccess({
            ...payload,
            output: res?.data?.downVoteComment?.modifiedComment
          });
        }),
        catchError(error => {
          console.error(error);
          return of(
            new homeActions.VoteReplyCommentError({
              ...payload,
              payload: 'An error occurred please try again'
            })
          );
        })
      );
    })
  );

  @Effect()
  deleteReplyComment$ = this.actions$.pipe(
    ofType<DeleteReplyComment>(HomeActionTypes.DeleteReplyComment),
    map(action => action?.payload),
    switchMap(payload => {
      const input: CommentInput = {
        commentID: payload?.replyCommentID
      };

      return this.postService.deleteComment(input).pipe(
        map(res => {
          if (res?.errors) {
            return new homeActions.DeleteReplyCommentError({
              ...payload,
              payload: res?.errors[0]?.message
            });
          }
          return new homeActions.DeleteReplyCommentSuccess({
            ...payload
          });
        }),
        catchError(error => {
          console.error(error);
          return of(
            new homeActions.DeleteReplyCommentError({
              ...payload,
              payload: 'Error occurred please try again'
            })
          );
        })
      );
    })
  );
}
